const API_BASE = "";

export interface Conera {
  id: string;
  name: string;
  client_version: number;
  latest_version: number;
  latest_filename: string | null;
  latest_uploaded_at: number | null;
  downloaded_version: number;
  last_downloaded_at: number | null;
  last_check_at: number | null;
}

export interface FileEntry {
  id: string;
  filename: string;
  version: number;
  size: number;
  uploaded_at: number;
  uploaded_by: string;
}

export interface DownloadEntry {
  version: number;
  checked_at: number;
}

export async function fetchConeras(): Promise<Conera[]> {
  const res = await fetch(`${API_BASE}/api/coneras`);
  if (!res.ok) throw new Error("Failed to fetch coneras");
  const data = await res.json();
  return data.coneras;
}

export async function createConera(id: string, name?: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/coneras`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, name }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Error" }));
    throw new Error(err.error || "Failed to create conera");
  }
}

export async function updateConera(id: string, name: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/coneras/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Error" }));
    throw new Error(err.error || "Failed to update conera");
  }
}

export async function deleteConera(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/coneras/${id}`, { method: "DELETE" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Error" }));
    throw new Error(err.error || "Failed to delete conera");
  }
}

export async function fetchConeraHistory(coneraId: string): Promise<{ files: FileEntry[]; downloads: DownloadEntry[] }> {
  const res = await fetch(`${API_BASE}/api/coneras/${coneraId}/history`);
  if (!res.ok) throw new Error("Failed to fetch history");
  return res.json();
}

export interface FileInfo {
  id: string;
  filename: string;
  size: number;
  version: number;
}

export interface VersionGroup {
  version: number;
  description: string;
  files: FileInfo[];
  uploaded_at: number;
  uploaded_by: string;
}

export async function fetchVersions(): Promise<VersionGroup[]> {
  const res = await fetch(`${API_BASE}/api/files`);
  if (!res.ok) throw new Error("Failed to fetch files");
  const data = await res.json();
  return data.versions;
}

export async function deleteVersion(version: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/versions/${version}`, { method: "DELETE" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Error" }));
    throw new Error(err.error || "Failed to delete version");
  }
}

export async function downloadFile(fileId: string, filename: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/download/${fileId}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Error al descargar" }));
    throw new Error(err.error || "Error al descargar " + filename);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export interface Stats {
  downloadsByDay: { day: number; count: number }[];
  versions: { version: number; uploaded_at: number; description: string }[];
  coneraActivity: { id: string; total_checks: number; last_check: number; last_version: number }[];
  totalDownloads: number;
  uniqueConeras: number;
}

export async function fetchStats(): Promise<Stats> {
  const res = await fetch(`${API_BASE}/api/stats`);
  if (!res.ok) throw new Error("Failed to fetch stats");
  return res.json();
}

export async function uploadFiles(
  files: File[],
  description?: string,
  signal?: AbortSignal
): Promise<{ filename: string; version: number }[]> {
  const formData = new FormData();
  files.forEach((f) => formData.append("files", f));
  if (description) formData.append("description", description);

  const res = await fetch(`${API_BASE}/api/upload`, {
    method: "POST",
    body: formData,
    signal,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Upload failed" }));
    throw new Error(err.error || "Upload failed");
  }
  const data = await res.json();
  return data.files;
}
